import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import api from "../lib/api";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 15;

export default function PaymentReturn() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const navigate = useNavigate();
    const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
    const [reservation, setReservation] = useState(null);
    const attempts = useRef(0);

    useEffect(() => {
        if (!sessionId) { setStatus("failed"); return; }
        let cancel = false;

        const poll = async () => {
            if (cancel) return;
            if (attempts.current >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
            attempts.current += 1;
            try {
                const r = await api.get(`/checkout/status/${sessionId}`);
                setReservation({ reservation_id: r.data.reservation_id, amount: r.data.amount_total });
                if (r.data.payment_status === "paid") {
                    setStatus("paid");
                    return;
                }
                if (r.data.status === "expired") { setStatus("failed"); return; }
                setTimeout(poll, POLL_INTERVAL);
            } catch {
                setStatus("failed");
            }
        };
        poll();
        return () => { cancel = true; };
    }, [sessionId]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-ink px-6 pt-20 grain" data-testid="payment-return-page">
            <div className="max-w-lg w-full">
                {status === "polling" && (
                    <div className="bg-ink-surface border border-white/10 rounded-sm p-10 text-center" data-testid="payment-polling">
                        <Clock size={56} className="text-gold mx-auto mb-4 animate-pulse" strokeWidth={1} />
                        <h1 className="font-serif text-3xl text-white">Verifica pagamento...</h1>
                        <p className="text-gray-400 mt-3 text-sm">Stiamo confermando la tua prenotazione. Non chiudere questa pagina.</p>
                        <div className="mt-6 w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
                    </div>
                )}
                {status === "paid" && (
                    <div className="bg-ink-surface border border-emerald-500/30 rounded-sm p-10 text-center" data-testid="payment-success">
                        <CheckCircle2 size={64} className="text-emerald-400 mx-auto mb-4" strokeWidth={1} />
                        <h1 className="font-serif text-4xl text-white">Pagamento ricevuto</h1>
                        <p className="text-gray-400 mt-3">La tua prenotazione è confermata. Riceverai un'email con tutti i dettagli.</p>
                        <div className="flex gap-3 justify-center mt-8">
                            <button onClick={() => navigate("/my-reservations")} className="bg-gold text-black font-semibold px-6 py-3 rounded-sm" data-testid="go-reservations-btn">
                                Le mie prenotazioni
                            </button>
                            <Link to="/" className="border border-white/20 text-white px-6 py-3 rounded-sm hover:border-gold">Home</Link>
                        </div>
                    </div>
                )}
                {(status === "failed" || status === "timeout") && (
                    <div className="bg-ink-surface border border-red-500/30 rounded-sm p-10 text-center" data-testid="payment-failed">
                        <XCircle size={56} className="text-red-400 mx-auto mb-4" strokeWidth={1} />
                        <h1 className="font-serif text-3xl text-white">{status === "timeout" ? "Verifica in corso" : "Pagamento non riuscito"}</h1>
                        <p className="text-gray-400 mt-3 text-sm">
                            {status === "timeout" ? "Riceverai una conferma via email appena il pagamento sarà verificato." : "Non siamo riusciti a confermare il pagamento. Riprova o contatta il ristorante."}
                        </p>
                        <div className="flex gap-3 justify-center mt-8">
                            <Link to="/my-reservations" className="bg-gold text-black font-semibold px-6 py-3 rounded-sm">Le mie prenotazioni</Link>
                            <Link to="/" className="border border-white/20 text-white px-6 py-3 rounded-sm hover:border-gold">Home</Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
